// XP and levels: a port of the app's lib/services/xp_service.dart. The server
// (fitviz_cloud xp.py, sync_xp) pays the XP; this mirrors its level curve and
// asks it for a sync after each log.
import { syncXp } from './cloud';

/** What paid the XP, in display order (xp.py _PARTS). */
export const XP_SOURCES = ['meals', 'protein', 'water', 'workout', 'week'];

/** Level titles, lowest first. The founder title is separate (xp.founder). */
export const XP_TITLES = [
  { id: 'rookie', level: 1 },
  { id: 'committed', level: 5 },
  { id: 'ironGrip', level: 10 },
  { id: 'discipline', level: 15 },
  { id: 'gymRat', level: 20 },
  { id: 'steelWill', level: 25 },
  { id: 'legend', level: 30 },
];

export const XP_FOUNDER_TITLE = 'founder';

// XP from level n to n+1 (index n-1), then a flat cost. Mirrors xp.LEVEL_COSTS.
const LEVEL_COSTS = [
  100, 150, 200, 250, 300, 400, 500, 650, 850, 1100,
  1300, 1500, 1700, 1900, 2100, 2300, 2500, 2700, 2900, 3200,
  3500, 3800, 4100, 4400,
];
const LEVEL_COST_AFTER = 4500;

export const levelCost = (level) => (level <= LEVEL_COSTS.length ? LEVEL_COSTS[level - 1] : LEVEL_COST_AFTER);

/** Total XP at which [level] starts. */
export function levelStart(level) {
  let total = 0;
  for (let l = 1; l < level; l += 1) total += levelCost(l);
  return total;
}

export function levelFor(total) {
  let level = 1;
  let need = 0;
  while (total >= need + levelCost(level)) {
    need += levelCost(level);
    level += 1;
  }
  return level;
}

/** XP and level from the user doc's `xp` (written only by the server). */
export function xpState(user) {
  const xp = user?.xp && typeof user.xp === 'object' ? user.xp : {};
  const total = Math.max(0, Math.round(Number(xp.total) || 0));
  const level = levelFor(total);
  const into = total - levelStart(level);
  const cost = levelCost(level);
  return {
    total,
    level,
    founder: xp.founder === true,
    into,
    cost,
    toNext: cost - into,
    progress: into / cost,
    title: [...XP_TITLES].reverse().find((t) => t.level <= level),
  };
}

/** The title [level] unlocks, if any. */
export const titleAt = (level) => XP_TITLES.find((t) => t.level === level) || null;

// Taps that land together (water glasses, a meal and its protein) become one call.
const DEBOUNCE_MS = 600;
let timer = null;
let running = Promise.resolve();

/**
 * Asks the server to pay new XP, shortly. What it paid is announced with a
 * `gm:xp-earned` window event ({gains, levelUps, xp}), which WebApp celebrates.
 * `immediate` skips the wait, for one-off logs (a meal, a finished workout)
 * whose celebration shouldn't lag.
 */
export function requestXpSync({ immediate = false } = {}) {
  clearTimeout(timer);
  timer = setTimeout(() => {
    running = running.then(async () => {
      try {
        const result = await syncXp();
        if (Array.isArray(result?.gains) && result.gains.length) {
          window.dispatchEvent(new CustomEvent('gm:xp-earned', { detail: result }));
        }
      } catch (e) {
        // best-effort: the next sync (next log, page focus) pays it
      }
    });
  }, immediate ? 0 : DEBOUNCE_MS);
}
